import { neverHere } from "@travellocal/utils/tsHelpers";
import { ContentTypeEnumHelper } from "./enumHelper";
import { formatQueryString } from "./urlHelpers";

interface EnquiryFormRouteParams {
  [key: string]: number | string | undefined;
  locationSlug?: string;
  itineraryId?: number;

  /**
   * #599 Each different route into the enquiry form should pass in a unique campaign string.
   */
  campaign: string;
}

export const customerRoutePrefix = "/customer";
export const ltoRoutePrefix = "/lto";
export const itineraryRoutePrefix = "/itinerary";
export const aboutRoutePrefix = "/about-us";
export const enquiryRoutePrefix = "/plan-your-trip";
export const destinationRoutePrefix = "/destinations";

export const aboutHome = () => aboutRoutePrefix;
export const aboutMeetTheTeam = () => `${aboutRoutePrefix}/meet-the-team`;
export const aboutHowItWorks = () => `${aboutRoutePrefix}#how-it-works`;
export const aboutOurStory = () => `${aboutRoutePrefix}#our-story`;
export const aboutLocalExperts = () => `${aboutRoutePrefix}#our-local-experts`;

export const sampleItinerary = (itinerarySlug: string) => `${itineraryRoutePrefix}/${itinerarySlug}`;

export const contentDetail = (content: IContent, withContentSlug = true) => {
  switch (content.type) {
    case ContentType.SampleItinerary:
      return sampleItinerary(content.slug);
    default:
      // eslint-disable-next-line no-case-declarations
      const base = `/inspiration/${ContentTypeEnumHelper.toSlug(content.type)}/${content.id}`;

      // NB For Disqus or social share, the URLs should never change once generated,
      // so don't add the slug in case they do.
      return withContentSlug ? base + `/${content.slug}` : base;
  }
};

/**
 * Route for the inspiration area.
 * @param fset Filters to be applied
 * @param page Which page to navigate to
 */
export const inspiration = (filters: IListContentParams = {}, page = 1, fromDropdown = false) => {
  // [ 15, 3, 6 ] => "3,6,15"
  const formatIds = (ids: number[]) => ids.filter(id => id != null && !isNaN(id)).sort((a, b) => a - b).join(",");

  const { types = [], locations = [], themes = [], months = [], statuses = [] } = filters;
  const typesQuery = formatIds(types);
  const locationsQuery = formatIds(locations);
  const themesQuery = formatIds(themes);
  const monthsQuery = formatIds(months);
  const statusesQuery = formatIds(statuses);

  const qs = [
    typesQuery.length > 0 ? `types=${typesQuery}` : null,
    locationsQuery.length > 0 ? `locations=${locationsQuery}` : null,
    themesQuery.length > 0 ? `themes=${themesQuery}` : null,
    monthsQuery.length > 0 ? `months=${monthsQuery}` : null,
    statusesQuery.length > 0 ? `statuses=${statusesQuery}` : null,
    page > 1 ? `page=${page}` : null,
    fromDropdown ? `dropdown=true` : null,
  ].filter(x => x != null);

  return qs.length > 0 ? `/inspiration?${qs.join("&")}` : "/inspiration";
}

export const enquiryForm = (params: EnquiryFormRouteParams) => {
  const { locationSlug, ...qsParams } = params;

  const route = locationSlug != null
    ? `${enquiryRoutePrefix}/${locationSlug}`
    : enquiryRoutePrefix;

  return qsParams
    ? route + formatQueryString(qsParams)
    : route;
}

export const login = (loggedOutReason?: string) => {
  let route = "/account/login";
  if (loggedOutReason && loggedOutReason.length > 0) {
    route += `?loggedOutReason=${loggedOutReason}`;
  }
  return route;
};

export const logout = () => "/account/logout";

export const customerIndex = () => customerRoutePrefix;

/**
 * Route to view an itinerary in the customer area.
 * If an itineraryId is not provided, this route will redirect to the most recently sent itinerary
 */
export const customerEnquiryItinerary = (enquiryId: number, quoteId?: number) => {
  return quoteId
    ? `${customerRoutePrefix}/enquiries/${enquiryId}/itinerary/${quoteId}`
    : `${customerRoutePrefix}/enquiries/${enquiryId}/itinerary`;
}

export const customerEnquiryMessaging = (enquiryId: number) => {
  return `${customerRoutePrefix}/enquiries/${enquiryId}/messages`;
}

export const customerEnquiryPayment = (enquiryId: number) => {
  return `${customerRoutePrefix}/enquiries/${enquiryId}/payment`;
};

export const customerEnquiryMakePayment = (enquiryId: number) => {
  return `${customerRoutePrefix}/enquiries/${enquiryId}/make-payment`;
}

export const customerEnquiryBookingTerms = (enquiryId: number) => {
  return `${customerRoutePrefix}/enquiries/${enquiryId}/booking-terms`;
};

export const ltoPtr = (ltoSlug: string) => `${ltoRoutePrefix}/${ltoSlug}/ptr`;

export const termsOfUse = () => "/about/terms";

export const termsAndConditions = () => "/about/terms-and-conditions";

export const index = () => "/";

export const becomeAPartner = () => "/about/become-a-partner";

export const contactUs = () => "/about/contact";

export const privacy = () => "/about/privacy";

export const accessibility = () => "/about/accessibility";

export const destinations = () => destinationRoutePrefix;

export const region = (regionSlug: string) => `${destinationRoutePrefix}/${regionSlug}`;

export const regionGallery = (regionSlug: string) => `${destinationRoutePrefix}/${regionSlug}/gallery`;
export const regionGalleryImage =
  (regionSlug: string, fileName: string) => `${destinationRoutePrefix}/${regionSlug}/gallery/${fileName}`;


export const country = (countrySlug: string, regionSlug: string) => `${destinationRoutePrefix}/${regionSlug}/${countrySlug}`;

export const countryExperts = (countrySlug: string, regionSlug: string) => `${destinationRoutePrefix}/${regionSlug}/${countrySlug}/our-experts`;
export const countryGallery = (countrySlug: string, regionSlug: string) => `${destinationRoutePrefix}/${regionSlug}/${countrySlug}/gallery`;
export const countryGalleryImage = (countrySlug: string, fileName: string, regionSlug: string) => `${destinationRoutePrefix}/${regionSlug}/${countrySlug}/gallery/${fileName}`;
export const countryReviews = (countrySlug: string, regionSlug: string) => `${destinationRoutePrefix}/${regionSlug}/${countrySlug}/reviews`;
export const countryTripIdeas = (countrySlug: string, regionSlug: string) => `${destinationRoutePrefix}/${regionSlug}/${countrySlug}/trip-ideas`;

export const destination = (destinationSlug: string) => `${destinationRoutePrefix}/${destinationSlug}`;

export const destinationGallery = (destinationSlug: string) => `${destinationRoutePrefix}/${destinationSlug}/gallery`;
export const destinationGalleryImage =
  (destinationSlug: string, fileName: string) => `${destinationRoutePrefix}/${destinationSlug}/gallery/${fileName}`;

export const location = (l: ILocation | ILocationSummary) => {
  switch (l.locationType) {
    case LocationType.Region:
      return region(l.slug);
    case LocationType.Country:
      return country(l.slug, l.parent.slug);
    case LocationType.Destination:
    case LocationType.Airport:
      return destination(l.slug);
    default:
      return neverHere(l, "location type routesInvoker");
  }
};
